import PropTypes from 'prop-types';

import {
  Chip,
  Stack,
  Alert,
  Dialog,
  Button,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import Iconify from 'src/components/iconify';
import { ListingTextLine, ListingThumbnail } from 'src/components/listing-row/listing-row';

import { SEVERITY_ICONS } from './utils/bulk-results-copy';
import { listingsWord } from './utils/listing-count-words';


// How each outcome reads on screen. 'skipped' is not an error: the item was not up on any of the
// accounts it was to come down from, so there was nothing to do. 'partial' is the one that still
// needs the seller — the item is down from some accounts but still up on others.
const OUTCOMES = {
  success: { label: 'Delisted', color: 'success', icon: 'solar:check-circle-bold' },
  partial: { label: 'Partly delisted', color: 'warning', icon: 'solar:danger-triangle-bold' },
  skipped: { label: 'Nothing to delist', color: 'default', icon: 'solar:info-circle-bold' },
  failed: { label: 'Not delisted', color: 'error', icon: 'solar:close-circle-bold' },
};

/**
 * What the run did, in one sentence — the same words as the toast that announced it, so the
 * seller reads one story, not two.
 */
function summarise({ total, success = 0, partial = 0, skipped = 0, failed = 0 }) {
  if (success === total) {
    return { severity: 'success', message: `${listingsWord(success)} delisted` };
  }
  if (failed === total) {
    return {
      severity: 'error',
      message: `Couldn't delist ${listingsWord(failed)}, so ${failed === 1 ? "it's" : "they're"} still for sale. Try again.`,
    };
  }

  const parts = [];
  if (success) parts.push(`${listingsWord(success)} delisted`);
  if (partial) parts.push(`${listingsWord(partial)} partly delisted`);
  if (skipped) parts.push(`${listingsWord(skipped)} had nothing to delist`);
  if (failed) parts.push(`${listingsWord(failed)} couldn't be delisted`);
  return { severity: 'warning', message: `${parts.join(', ')}.` };
}

/**
 * The report a seller reads after a bulk delist.
 *
 * Every item says what happened to it in plain words on its own row. The two notices above the
 * rows appear only when they apply: items still being taken down in this browser tab, and items
 * still up on an account Voolist can't reach.
 */
export default function BulkDelistResultsDialog({ open, onClose, results }) {
  if (!results) return null;

  const details = results.details || [];
  const summary = summarise(results);

  // The accounts with no server-side connection (Depop, Grailed, Whatnot, OfferUp, Vestiaire): their items are
  // taken off sale through the extension, in this tab — deleted on Depop and Grailed, hidden on Whatnot, archived
  // on OfferUp, withdrawn on Vestiaire, as each item's own line says. Named as those lines name them: by the
  // account's name the server gives (accountDisplayName), never a marketplace id.
  const handledInBrowser = [...new Set(details.flatMap((detail) => detail.browserAccounts || []))];

  // Accounts Voolist can't reach right now, where the items are therefore still up. Only the
  // seller can close that gap.
  const stillLive = [...new Set(details.flatMap((detail) => detail.stillLiveUnreachable || []))];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Iconify
            icon={SEVERITY_ICONS[summary.severity]}
            width={24}
            sx={{ color: `${summary.severity}.main` }}
          />
          <span>Delist results</span>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Alert severity={summary.severity}>{summary.message}</Alert>

          {handledInBrowser.length > 0 && (
            <Alert severity="info">
              On {handledInBrowser.join(' and ')}, listings come off one at a time from this page. Keep
              it open until it&apos;s done.
            </Alert>
          )}

          {stillLive.length > 0 && (
            <Alert severity="warning">
              Still for sale on <strong>{stillLive.join(', ')}</strong>, which Voolist can&apos;t
              reach. Reconnect {stillLive.length === 1 ? 'that account' : 'those accounts'} on the
              Marketplaces page, or take those listings off there yourself.
            </Alert>
          )}

          <Stack spacing={1}>
            {details.map((detail) => {
              const outcome = OUTCOMES[detail.status] || OUTCOMES.failed;

              return (
                <Stack
                  key={detail.listingId}
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover' }}
                >
                  <ListingThumbnail src={detail.thumbnail} alt={detail.title} size={40} rounded={1} />

                  <ListingTextLine
                    listing={{ title: detail.title }}
                    secondLine={
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {detail.message}
                      </Typography>
                    }
                  />

                  <Chip
                    icon={<Iconify icon={outcome.icon} width={16} />}
                    label={outcome.label}
                    color={outcome.color}
                    size="small"
                    sx={{ flexShrink: 0, minWidth: 110 }}
                  />
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BulkDelistResultsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  results: PropTypes.shape({
    total: PropTypes.number,
    success: PropTypes.number,
    partial: PropTypes.number,
    skipped: PropTypes.number,
    failed: PropTypes.number,
    details: PropTypes.arrayOf(
      PropTypes.shape({
        listingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        thumbnail: PropTypes.string,
        listedOn: PropTypes.arrayOf(PropTypes.string),
        status: PropTypes.oneOf(['success', 'partial', 'skipped', 'failed']),
        message: PropTypes.string,
        // The accounts the item came down from...
        delistedFrom: PropTypes.arrayOf(PropTypes.string),
        // ...the ones the extension is still taking it down from, in this tab...
        browserAccounts: PropTypes.arrayOf(PropTypes.string),
        // ...the ones that refused, and why...
        failedAccounts: PropTypes.arrayOf(
          PropTypes.shape({ account: PropTypes.string, message: PropTypes.string })
        ),
        // ...and the ones it stays up on because Voolist can't reach them.
        stillLiveUnreachable: PropTypes.arrayOf(PropTypes.string),
      })
    ),
  }),
};
